import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { DOCTOR, CONTACT_NUMBERS } from "@/data/static";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name:"", email:"", phone:"", message:"" });
  const [loading, setLoading] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back within 24 hours.");
      setForm({ name:"", email:"", phone:"", message:"" });
    } catch(err) {
      toast.error("Failed to send. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <main className="pt-32 pb-16" data-testid="contact-page">
      <div className="container-x">
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6}}>
          <span className="overline">Contact</span>
          <h1 className="font-heading text-5xl sm:text-6xl tracking-tight font-light mt-3">Let's <em className="italic font-medium text-primary">talk</em></h1>
        </motion.div>
        <div className="mt-12 grid lg:grid-cols-2 gap-12">
          <div className="space-y-7">
            <div className="rounded-3xl overflow-hidden aspect-[16/10] shadow-soft border border-slate-100">
              <iframe title="map" className="w-full h-full" loading="lazy" src="https://maps.google.com/maps?q=Grand+Trunk+Road+Sasaram+Bihar+821115&output=embed"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {icon:MapPin,title:"Address",value:DOCTOR.address},
                {icon:Phone,title:"Appointments",value:DOCTOR.phone,href:`tel:${DOCTOR.phoneRaw}`},
                {icon:Mail,title:"Email",value:DOCTOR.email,href:`mailto:${DOCTOR.email}`},
                {icon:MessageCircle,title:"WhatsApp",value:"Chat with us",href:`https://wa.me/${DOCTOR.whatsapp}`},
              ].map((it,i)=>{
                const Icon = it.icon;
                const inner = (
                  <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-soft hover:border-primary/30 transition-colors h-full">
                    <Icon className="w-5 h-5 text-primary"/>
                    <div className="mt-3 text-xs uppercase tracking-wider text-slate-500">{it.title}</div>
                    <div className="text-sm text-slate-800 mt-1">{it.value}</div>
                  </div>
                );
                return it.href ? <a key={i} href={it.href} target="_blank" rel="noreferrer">{inner}</a> : <div key={i}>{inner}</div>;
              })}
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-soft" data-testid="contact-numbers">
              <div className="text-xs uppercase tracking-wider text-slate-500">All Contact Numbers</div>
              <ul className="mt-3 divide-y divide-slate-100">
                {CONTACT_NUMBERS.map(n => (
                  <li key={n.raw} className="py-2.5 flex items-center justify-between text-sm">
                    <span className="text-slate-600">{n.label}</span>
                    <a href={`tel:${n.raw}`} className="font-medium text-primary hover:underline">{n.number}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <form onSubmit={submit} className="glass rounded-3xl p-8 shadow-glass space-y-5" data-testid="contact-form">
            <div className="font-heading text-2xl font-medium">Send us a message</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input required value={form.name} onChange={e=>set("name",e.target.value)} data-testid="contact-name" className="mt-2"/></div>
              <div><Label>Email</Label><Input type="email" required value={form.email} onChange={e=>set("email",e.target.value)} data-testid="contact-email" className="mt-2"/></div>
            </div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e=>set("phone",e.target.value)} data-testid="contact-phone" className="mt-2"/></div>
            <div><Label>Message</Label><Textarea rows={5} required value={form.message} onChange={e=>set("message",e.target.value)} data-testid="contact-message" className="mt-2"/></div>
            <Button type="submit" disabled={loading} className="rounded-full bg-primary hover:bg-primary-dark px-7 py-6" data-testid="contact-submit">{loading?"Sending…":"Send Message"}</Button>
          </form>
        </div>
      </div>
    </main>
  );
}
